import React, { useState } from 'react';

/**
 * Pure derivation: reasoning JSONB → human-readable summary string.
 *
 * Returns `null` when there's nothing to display (null reasoning, unknown path,
 * or missing details). Caller should treat null as "render nothing".
 *
 * Exported separately so it can be unit-tested without a React renderer.
 */
export function deriveReasoningSummary(reasoning) {
  if (!reasoning || typeof reasoning !== 'object') return null;
  const { path, details } = reasoning;
  if (!path) return null;
  const d = details || {};

  switch (path) {
    // ----- project paths -----
    case 'ascent': {
      if (!d.matter_number) return null;
      const where =
        d.matched_in === 'vendor_invoice_subject'
          ? 'in invoice subject'
          : 'in extracted text';
      return `Matched ASCENT matter ${d.matter_number} (${where})`;
    }
    case 'keyword': {
      const score =
        typeof d.score === 'number' ? d.score.toFixed(2) : null;
      const kws = Array.isArray(d.matched_keywords) ? d.matched_keywords : [];
      // Top 2-3 by weight (descending). Stable for ties via original order.
      const top = [...kws]
        .sort((a, b) => (b?.weight ?? 0) - (a?.weight ?? 0))
        .slice(0, 3)
        .map((k) => k?.keyword)
        .filter(Boolean);
      const kwPart = top.length ? ` — ${top.join(', ')}` : '';
      const scorePart = score != null ? `Keyword score ${score}` : 'Keyword match';
      return `${scorePart}${kwPart}`;
    }
    case 'ai': {
      const reason = (d.reason || '').toString().trim();
      if (!reason) return 'AI fallback';
      return `AI: '${reason}'`;
    }
    case 'unclassified': {
      const reason = (d.reason || '').toString().trim();
      return reason
        ? `Unclassified: ${reason}`
        : 'No project candidates matched';
    }

    // ----- vendor paths -----
    case 'exact_name': {
      if (!d.matched_name) return 'Exact name match';
      return `Exact name match: ${d.matched_name}`;
    }
    case 'alias': {
      if (!d.matched_alias) return 'Matched alias';
      return `Matched alias '${d.matched_alias}'`;
    }
    case 'fuzzy_normalize': {
      const from = d.extracted;
      const to = d.matched_vendor_name;
      if (!from || !to) return 'Fuzzy match';
      return `Fuzzy match: '${from}' → '${to}'`;
    }
    case 'new_vendor': {
      const name = d.extracted;
      return name ? `New vendor: '${name}'` : 'New vendor';
    }

    default:
      return null;
  }
}

function ClassificationReasoning({ kind, reasoning }) {
  const [expanded, setExpanded] = useState(false);

  // Historical invoice (pre-migration) — render nothing.
  if (!reasoning) return null;

  const summary = deriveReasoningSummary(reasoning);
  if (!summary) return null;

  // Always hide the explanation behind a "Why?" chip — keeps the row tidy
  // for both short (vendor) and long (AI) summaries. Click reveals the
  // full text in a panel underneath.
  return (
    <span
      className="classification-reasoning"
      data-kind={kind}
      data-path={reasoning.path}
    >
      <button
        type="button"
        className="classification-reasoning-chip"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide' : 'Why?'}
      </button>
      {expanded && (
        <div className="classification-reasoning-panel">{summary}</div>
      )}
    </span>
  );
}

export default ClassificationReasoning;
